import React from 'react';
import PropTypes from 'prop-types';
import TripItem from '../TripItem';
import ResultsTitle from '../../../ui/atoms/ResultsTitle';
import { getTripTotal } from '../../../utils/seats';

/**
 * ClosedTripsSection component.
 * Renders a section displaying closed/unavailable trips (sold out or departed).
 * @param {object} props - trips, filtersApplied, features, origin, destination.
 * @param {Array} props.closedTrips - Array of closed trip objects.
 * @param {Function} props.selectTrip - Function to handle trip selection (disabled for closed trips).
 * @param {boolean} props.isRoundTrip - Whether this is a round trip search.
 * @param {string} props.way - Trip direction ('departure' or 'return').
 * @param {boolean} props.isLogged - Whether user is logged in.
 * @param {object} props.departureTrip - Departure trip data (for round trips).
 * @param {object} props.rewardTicketStatus - Reward ticket status.
 * @param {Function} props.setRewardTicketStatus - Function to set reward ticket status.
 * @param {object} props.filtersApplied - Applied filters object.
 * @param {object} props.features - Feature flags object.
 * @param {object} props.origin - Origin location object.
 * @param {object} props.destination - Destination location object.
 * @returns {JSX.Element|null} The rendered ClosedTripsSection component or null if no closed trips.
 */
const ClosedTripsSection = ({
  closedTrips,
  selectTrip,
  isRoundTrip,
  way,
  isLogged,
  departureTrip,
  rewardTicketStatus,
  setRewardTicketStatus,
  filtersApplied,
  features,
  origin,
  destination,
}) => {
  if (!closedTrips || closedTrips.length === 0) return null;

  return (
    <div className="trip-container">
      <ResultsTitle sectionTitle="closedSchedules" />

      <div className="space-y-3">
        {closedTrips.map((trip, index) => (
          <TripItem
            key={trip.id}
            trip={trip}
            id={trip.id}
            position={index}
            selectTrip={selectTrip}
            availability={0}
            isRoundTrip={isRoundTrip}
            way={way}
            isLogged={isLogged}
            departureTrip={departureTrip}
            rewardTicketStatus={rewardTicketStatus}
            setRewardTicketStatus={setRewardTicketStatus}
            total={getTripTotal({ trip, way, isRoundTrip })}
            filtersApplied={filtersApplied}
            features={features}
            origin={trip.origin || origin}
            destination={trip.destination || destination}
            arrival={trip.arrival}
            departure={trip.departure}
            duration={trip.duration}
            serviceType={trip.line?.serviceType}
            availableCategories={trip.availableCategories || []}
            isClosed
          />
        ))}
      </div>
    </div>
  );
};

ClosedTripsSection.propTypes = {
  closedTrips: PropTypes.array,
  selectTrip: PropTypes.func.isRequired,
  isRoundTrip: PropTypes.bool,
  way: PropTypes.string.isRequired,
  isLogged: PropTypes.bool,
  departureTrip: PropTypes.object,
  rewardTicketStatus: PropTypes.object,
  setRewardTicketStatus: PropTypes.func,
  filtersApplied: PropTypes.object,
  features: PropTypes.object,
  origin: PropTypes.object,
  destination: PropTypes.object,
};

ClosedTripsSection.defaultProps = {
  closedTrips: [],
  isRoundTrip: false,
  isLogged: false,
  departureTrip: null,
  rewardTicketStatus: {},
  setRewardTicketStatus: () => {},
  filtersApplied: {},
  features: {},
  origin: null,
  destination: null,
};

export default ClosedTripsSection;
